<script setup lang="ts">
import { computed, onMounted, ref, Ref } from "vue";
import { mande, defaults } from "mande";

interface Props {
    class?: string;
    videoUrl: string;
}

const props = defineProps<Props>();

const thumbnail: Ref<null | {
    width: number;
    height: number;
    url: string;
}> = ref(null);
const videoId: Ref<String | null> = ref(null);
const clicked: Ref<boolean> = ref(true);

const videoType = computed(() => {
    if (props.videoUrl && props.videoUrl !== "") {
        if (props.videoUrl.includes("youtu")) {
            return "youtube";
        } else if (props.videoUrl.includes("vimeo")) {
            return "vimeo";
        } else if (props.videoUrl.includes("instagram")) {
            return "instagram";
        }
        return "unknown";
    }
});

const embedUrl = computed(() => {
    switch (videoType.value) {
        case "youtube":
            return `https://www.youtube.com/embed/${videoId.value}?autoplay=1&modestbranding=1`;
        case "vimeo":
            return `https://player.vimeo.com/video/${videoId.value}?autoplay=1&loop=true&portrait=false&title=false`;
        default:
            return "";
    }
});

const getYoutubeVideoData = () => {
    // get video id from /ember or /watch url
    if (props.videoUrl!.includes("embed")) {
        videoId.value = props.videoUrl!.split("embed/")[1].split("&")[0];
    } else if (props.videoUrl!.includes("watch")) {
        videoId.value = props.videoUrl!.split("v=")[1].split("&")[0];
    } else if (props.videoUrl!.includes("shorts")) {
        videoId.value = props.videoUrl!.split("shorts/")[1].split("?")[0];
    } else if (props.videoUrl!.includes("youtu.be")) {
        videoId.value = props.videoUrl!.split("youtu.be/")[1].split("?")[0];
    } else {
        console.error("unknown youtube url");
    }
    // get thumbnail from youtube
    mande(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId.value}&format=json`)
        .get()
        // @ts-ignore
        .then((response: { thumbnail_url: string; thumbnail_width: number; thumbnail_height: number; width: number; height: number }) => {
            thumbnail.value = {
                width: response.width,
                height: response.height,
                url: response.thumbnail_url,
            };
        });
};

const getVimeoVideoData = () => {
    videoId.value = props.videoUrl!.split("vimeo.com/")[1].split("&")[0];

    const endpoint = "https://vimeo.com/api/oembed.json";

    fetch(`${endpoint}?url=${encodeURIComponent(props.videoUrl)}`, {
        method: "get",
        cache: "no-cache",
        mode: "cors",
    })
        .then((response) => {
            return response.json();
        })
        .then(
            (response: {
                type: string;
                version: string;
                provider_name: string;
                provider_url: string;
                title: string;
                author_name: string;
                author_url: string;
                is_plus: string;
                account_type: string;
                html: string;
                width: number;
                height: number;
                duration: number;
                description: string;
                thumbnail_url: string;
                thumbnail_width: number;
                thumbnail_height: number;
                thumbnail_url_with_play_button: string;
                upload_date: string;
                video_id: number;
                uri: string;
            }) => {
                thumbnail.value = {
                    width: response.thumbnail_width,
                    height: response.thumbnail_height,
                    url: response.thumbnail_url.split("_")[0] + ".jpg",
                };
            },
        )
        .catch((err) => console.log("ajax error -> ", err));
};

const getInstagramVideoData = () => {
    // set dummy first image
    thumbnail.value = {
        width: 100,
        height: 190,
        url: "/",
    };

    // load async script script www.instagram.com/embed.js
    const script = document.createElement("script");
    script.src = "https://www.instagram.com/embed.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
};

const clearIubendaCookiesAndReset = () => {
    const cookies = document.cookie.split(";");
    const expiration = "Thu, 01 Jan 1970 00:00:01 GMT"; // Expire immediately
    const path = "/";

    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        const name = cookie.split("=")[0];

        if (name.startsWith("_iub_")) {
            document.cookie = name + "=; Path=" + path + "; Expires=" + expiration + ";";
            document.cookie = name + "=; Path=" + path + "; Domain=" + window.location.hostname + "; Expires=" + expiration + ";";
            document.cookie = name + "=; Path=" + path + "; Domain=." + window.location.hostname + "; Expires=" + expiration + ";";
        }
    }

    window.location.reload();
};

const isPurposeConsentGiven = (purposeId: any) => {
    // @ts-ignore
    if (window._iub && window._iub.cs && window._iub.cs.consent && window._iub.cs.consent.purposes) {
        // @ts-ignore
        const purposes = window._iub.cs.consent.purposes;
        // @ts-ignore
        return purposes[purposeId] === true;
    }

    return false;
};

switch (videoType.value) {
    case "youtube":
        getYoutubeVideoData();
        break;
    case "instagram":
        getInstagramVideoData();
        break;
    case "vimeo":
        getVimeoVideoData();
        break;
}

let cookiesAccepted = ref(false);
onMounted(() => {
    setTimeout(() => {
        cookiesAccepted.value = isPurposeConsentGiven(4);
    }, 1000);
});
</script>

<template>
    <div v-if="thumbnail" class="w-full relative cursor-pointer" :class="class" :style="`aspect-ratio: ${thumbnail.width}/${thumbnail.height};`" @click.prevent="clicked = true">
        <template v-if="cookiesAccepted">
            <transition-group name="fade-absolute" mode="out-in">
                <img
                    v-if="!clicked && thumbnail"
                    class="w-full absolute inset-0 [&_+_div]:opacity-0 [&.lazyloaded_+_div]:opacity-100 h-full cursor-pointer object-cover object-center lazyload"
                    :data-src="thumbnail.url"
                    :alt="thumbnail.url"
                    :style="`aspect-ratio: ${thumbnail.width}/${thumbnail.height - 2};`"
                />
                <iframe v-else-if="embedUrl !== ''" class="absolute inset-0 w-full h-full" :src="embedUrl" allowtransparency allow="autoplay; fullscreen"></iframe>
            </transition-group>
            <transition name="fade" mode="out-in">
                <di v-if="!clicked" class="absolute flex items-center gap-[14px] transition-opacity justify-center flex-col inset-0 w-full h-full" style="background: linear-gradient(180deg, rgba(43, 59, 116, 0) 0%, rgba(43, 59, 116, 0.92) 100%)">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M5.59844 24.0004C5.59844 13.8384 13.8364 5.60039 23.9984 5.60039C34.1605 5.60039 42.3984 13.8384 42.3984 24.0004C42.3984 34.1624 34.1605 42.4004 23.9984 42.4004C13.8364 42.4004 5.59844 34.1624 5.59844 24.0004ZM23.9984 2.40039C12.0691 2.40039 2.39844 12.071 2.39844 24.0004C2.39844 35.9297 12.0691 45.6004 23.9984 45.6004C35.9278 45.6004 45.5984 35.9297 45.5984 24.0004C45.5984 12.071 35.9278 2.40039 23.9984 2.40039ZM20.886 14.6691C20.395 14.3418 19.7637 14.3113 19.2435 14.5897C18.7232 14.8681 18.3984 15.4103 18.3984 16.0004V32.0004C18.3984 32.5905 18.7232 33.1326 19.2435 33.4111C19.7637 33.6895 20.395 33.659 20.886 33.3317L32.886 25.3317C33.3311 25.0349 33.5984 24.5354 33.5984 24.0004C33.5984 23.4654 33.3311 22.9659 32.886 22.6691L20.886 14.6691ZM29.114 24.0004L21.5984 29.0108V18.99L29.114 24.0004Z"
                            fill="white"
                        />
                    </svg>

                    <span class="text-white font-semibold tracking-[-0.32px]">Video afspelen</span>
                </di>
            </transition>
        </template>
        <div v-else class="bg-black w-full h-full">
            <div class="absolute text-white top-0 bottom-0 text-center self-center w-full cursor-pointer">
                <span @click="clearIubendaCookiesAndReset()" class="text-md text-white underline hover:no-underline">Accepteer cookies</span>
                om deze video te kunnen bekijken.
            </div>
        </div>
    </div>
</template>

<style scoped>
.fade-enter-active {
    transition: all 0.25s ease-out !important;
}

.fade-leave-active {
    transition: all 0.25s cubic-bezier(1, 0.7, 0.8, 1) !important;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0 !important;
}

.fade-absolute-enter-active {
    position: absolute;
    transition: all 0.25s ease-out !important;
}

.fade-absolute-leave-active {
    position: absolute;
    transition: all 0.25s cubic-bezier(1, 0.7, 0.8, 1) !important;
}

.fade-absolute-enter-from,
.fade-absolute-leave-to {
    opacity: 0 !important;
}
</style>
